import React from 'react';
import {
  Avatar,
  Box,
  ButtonBase,
  Checkbox,
  Chip,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { alpha } from '@mui/material/styles';
import { getRoleDisplay } from '../../constants/roles';
import { getRoleColor, getRoleColorAlpha } from '../../assets/theme/roleColors';
import CareLogEntryCard from '../UI/CareLogEntryCard';

const TEXT_PRIMARY = '#1F2937';
const TEXT_SECONDARY = '#4B5563';
const TEXT_MUTED = '#6B7280';

const normalizeTags = (tags = []) => (
  Array.isArray(tags)
    ? tags
        .map((tag) => {
          if (!tag) return null;
          if (typeof tag === 'string' || typeof tag === 'number') {
            return { label: String(tag) };
          }
          if (typeof tag === 'object') {
            const label = String(tag.label || tag.text || '').trim();
            if (!label) return null;
            return {
              label,
              color: tag.color || null,
              bg: tag.bg || null,
              border: tag.border || null,
              title: tag.title || null,
            };
          }
          return null;
        })
        .filter(Boolean)
    : []
);

const getVariantStyles = (variant, accentColor, isNested, typePalette = null) => {
  const rootBg =
    typePalette?.bg || alpha(accentColor, variant === 'story' ? 0.05 : 0.035);
  const rootBorder =
    typePalette?.border || alpha(accentColor, 0.18);
  const iconBg = typePalette?.bg
    ? alpha(typePalette.dot || accentColor, variant === 'story' ? 0.12 : 0.1)
    : alpha(accentColor, 0.1);

  if (variant === 'story') {
      return {
        root: {
          borderRadius: '18px',
          border: `1px solid ${alpha('#DCE6E1', 0.9)}`,
          backgroundColor: '#FFFFFF',
          boxShadow: `0 10px 24px ${alpha('#23312D', 0.07)}`,
          px: { xs: 1.9, sm: 2.35 },
          py: { xs: 2.25, sm: 2.5 },
        },
      icon: {
        size: 18,
        radius: 2,
        bg: iconBg,
        color: accentColor,
      },
      title: {
        size: '1.0rem',
        weight: 700,
      },
      time: {
        size: '0.78rem',
        },
      };
  }

  if (variant === 'feed') {
    return {
      root: {
        borderRadius: '12px',
        border: `1px solid ${alpha(rootBorder, 0.7)}`,
        backgroundColor: alpha(rootBg || '#FFFFFF', 0.82),
        boxShadow: 'none',
        px: { xs: 0.95, sm: 1.05 },
        py: { xs: 0.85, sm: 0.95 },
      },
      icon: {
        size: 18,
        radius: 2,
        bg: alpha(accentColor, 0.1),
        color: accentColor,
      },
      title: {
        size: '0.92rem',
        weight: 740,
      },
      time: {
        size: '0.72rem',
      },
    };
  }

  return {
    root: {
      borderRadius: isNested ? '14px' : '16px',
      border: `1px solid ${rootBorder}`,
      backgroundColor: rootBg,
      px: { xs: 1.4, sm: 2 },
      py: { xs: 1.5, sm: 1.75 },
    },
    icon: {
      size: isNested ? 14 : 16,
      radius: '50%',
      bg: iconBg,
      color: accentColor,
    },
    title: {
      size: isNested ? '0.88rem' : '1.0rem',
      weight: 800,
    },
    time: {
      size: '0.78rem',
    },
  };
};

const getDensityStyles = (compact, variant, typePalette, resolvedAccentColor) => {
  const shellColor = typePalette?.bg || alpha(resolvedAccentColor, 0.035);
  const iconBg = typePalette?.bg
    ? alpha(typePalette.dot || resolvedAccentColor, compact ? 0.08 : 0.1)
    : alpha(resolvedAccentColor, compact ? 0.08 : 0.1);

  if (!compact) {
    return {
      root: {},
      icon: {},
      title: {},
      time: {},
      headerGap: 0.16,
      actionsGap: 0.42,
      actionsLeftMargin: 0.12,
      bodyMarginTop: 0.6,
      tagsMarginTop: 0.75,
      tagsGap: 0.2,
    };
  }

  if (variant === 'feed') {
    return {
      root: {
        backgroundColor: shellColor,
      },
      icon: {
        size: 16,
        bg: iconBg,
      },
      title: {
        size: '0.88rem',
      },
      time: {
        size: '0.66rem',
      },
      headerGap: 0.12,
      actionsGap: 0.28,
      actionsLeftMargin: 0.04,
      bodyMarginTop: 0.25,
      tagsMarginTop: 0.4,
      tagsGap: 0.12,
    };
  }

  return {
    root: {
      backgroundColor: shellColor,
      py: { xs: 0.18, sm: 0.22 },
    },
    icon: {
      size: 16,
      bg: iconBg,
    },
    title: {
      size: '0.84rem',
    },
    time: {
      size: '0.64rem',
    },
    headerGap: 0.12,
    actionsGap: 0.28,
    actionsLeftMargin: 0.04,
    bodyMarginTop: 0.0,
    tagsMarginTop: 0.0,
    tagsGap: 0.12,
  };
};

const TimelineEntryShell = ({
  id,
  dataEntryId,
  ariaLabel,
  typeKey = null,
  presentation = null,
  title,
  time,
  actor = null,
  headerNode = null,
  titleNode = null,
  titleAccessoryNode = null,
  metaNode = null,
  leadingNode = null,
  onHeaderClick = null,
  headerExpanded = null,
  headerExtras = null,
  tags = [],
  bodyNode = null,
  children = null,
  menuActions = [],
  showActions = false,
  actionMenuDisabled = false,
  accentColor = '#64748B',
  variant = 'default',
  nested = false,
  compact = false,
  sx = {},
}) => {
  const [actionsAnchorEl, setActionsAnchorEl] = React.useState(null);
  const actionsOpen = Boolean(actionsAnchorEl);

  const normalizedTags = React.useMemo(() => normalizeTags(tags), [tags]);
  const resolvedPresentation = presentation || {};
  const typePalette = React.useMemo(
    () => resolvedPresentation.timelineColors || null,
    [resolvedPresentation.timelineColors]
  );

  const resolvedAccentColor =
    typePalette?.dot || accentColor || '#64748B';
  const actorRoleColor = actor?.role ? getRoleColor(actor.role, 'primary') : resolvedAccentColor;
  const actorRoleBg = actor?.role ? getRoleColorAlpha(actor.role, 'primary', 0.14) : alpha(resolvedAccentColor, 0.08);
  const actorRoleBorder = actor?.role ? getRoleColorAlpha(actor.role, 'primary', 0.24) : alpha(resolvedAccentColor, 0.14);
  const actorRoleLabel = actor?.role ? getRoleDisplay(actor.role).plainLabel : '';
  const actorLabel = actor?.label || actor?.alt || '';
  const actorTooltip = actorLabel
    ? `Logged by ${actorLabel}${actorRoleLabel ? ` · ${actorRoleLabel}` : ''}`
    : actorRoleLabel
      ? `Logged by ${actorRoleLabel}`
      : 'Logged by';
  const shellTone = typePalette || {
    bg: alpha(resolvedAccentColor, 0.035),
    border: alpha(resolvedAccentColor, 0.18),
    dot: resolvedAccentColor,
  };
  const railColor = typePalette?.dot || resolvedAccentColor;

  const variantStyles = React.useMemo(
    () => getVariantStyles(variant, resolvedAccentColor, nested, typePalette),
    [variant, resolvedAccentColor, nested, typePalette]
  );
  const densityStyles = React.useMemo(
    () => getDensityStyles(compact, variant, typePalette, resolvedAccentColor),
    [compact, resolvedAccentColor, typePalette, variant]
  );
  const isStoryVariant = variant === 'story';

  const handleMenuOpen = React.useCallback((event) => {
    setActionsAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = React.useCallback(() => {
    setActionsAnchorEl(null);
  }, []);

  const handleAction = React.useCallback(
    (action) => {
      handleMenuClose();
      if (typeof action?.onClick === 'function') {
        action.onClick();
      }
    },
    [handleMenuClose]
  );

  const topRightNode = showActions && Array.isArray(menuActions) && menuActions.length > 0 ? (
    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <IconButton
        size="medium"
        onClick={handleMenuOpen}
        disabled={actionMenuDisabled}
        sx={{
          width: isStoryVariant ? 28 : compact ? 24 : 26,
          height: isStoryVariant ? 28 : compact ? 24 : 26,
          borderRadius: isStoryVariant ? '999px' : 1.2,
          bgcolor: 'transparent',
          color: TEXT_MUTED,
          border: '1px solid transparent',
          boxShadow: 'none',
          '& .MuiSvgIcon-root': {
            fontSize: 16,
          },
          '&:hover': {
            bgcolor: alpha('#64748B', 0.06),
            borderColor: alpha('#64748B', 0.1),
          },
        }}
        aria-label="Entry actions"
      >
        <MoreHorizIcon />
      </IconButton>

      <Menu
        anchorEl={actionsAnchorEl}
        open={actionsOpen}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
      >
        {menuActions.map((action, index) => (
          <MenuItem
            key={`${action?.label || 'action'}-${index}`}
            onClick={() => handleAction(action)}
            disabled={actionMenuDisabled || action?.disabled}
            sx={action?.danger ? { color: '#B42318' } : null}
          >
            {typeof action?.checked === 'boolean' ? (
              <Checkbox
                checked={action.checked}
                tabIndex={-1}
                disableRipple
                size="small"
                sx={{
                  p: 0,
                  mr: 1,
                  color: TEXT_MUTED,
                  '&.Mui-checked': {
                    color: action?.danger ? '#B42318' : TEXT_MUTED,
                  },
                }}
              />
            ) : action?.icon
              ? React.cloneElement(action.icon, {
                  sx: {
                    fontSize: 16,
                    mr: 1,
                    color: action?.danger ? '#B42318' : TEXT_MUTED,
                  },
                })
              : null}
            <ListItemText
              primary={action?.label || 'Action'}
              secondary={action?.description || null}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
              secondaryTypographyProps={{
                fontSize: '0.75rem',
                lineHeight: 1.3,
                color: TEXT_MUTED,
                sx: { whiteSpace: 'normal' },
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  ) : null;

  const titleLineNode = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.55,
        minWidth: 0,
        flexWrap: 'wrap',
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: isStoryVariant ? '0.84375rem' : (densityStyles.title.size || variantStyles.title.size),
          fontWeight: isStoryVariant ? 700 : variantStyles.title.weight,
          color: isStoryVariant ? '#1A2320' : TEXT_PRIMARY,
          lineHeight: 1.25,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </Typography>
      {titleAccessoryNode ? <Box sx={{ flexShrink: 0 }}>{titleAccessoryNode}</Box> : null}
    </Box>
  );

  const resolvedMetaNode = metaNode || titleNode || ((time || actor?.shortLabel || normalizedTags.length > 0 || headerExtras) ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        minWidth: 0,
        flexWrap: 'wrap',
      }}
    >
      {time ? (
        <Typography
          component="span"
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 500,
            color: '#4A5954',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
          }}
        >
          {time}
        </Typography>
      ) : null}

      {actor?.shortLabel ? (
        <>
          {time ? <Typography component="span" sx={{ fontSize: '0.6875rem', color: '#9CA3AF', lineHeight: 1 }}>·</Typography> : null}
          <Tooltip title={actorTooltip} arrow>
            <Box
              component="span"
              aria-label={actorTooltip}
              sx={{
                minWidth: 20,
                height: 20,
                px: 0.55,
                borderRadius: '999px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: actorRoleBg,
                border: `1px solid ${actorRoleBorder}`,
                fontSize: '0.6875rem',
                fontWeight: 700,
                color: actorRoleColor,
                lineHeight: 1.2,
                letterSpacing: '0.02em',
                whiteSpace: 'nowrap',
              }}
            >
              {actor.shortLabel}
            </Box>
          </Tooltip>
        </>
      ) : null}

      {normalizedTags.slice(0, 2).map((tag, index) => (
        <Chip
          key={`${tag.label}-${index}`}
          label={tag.label}
          size="small"
          title={tag.title || undefined}
          sx={{
            height: 20,
            px: 0.2,
            fontSize: '0.6875rem',
            fontWeight: 700,
            color: typePalette?.text || tag.color || TEXT_SECONDARY,
            bgcolor: alpha(typePalette?.strongBg || resolvedAccentColor, 0.66),
            border: 'none',
            borderRadius: '999px',
            '& .MuiChip-label': {
              px: 0.7,
            },
          }}
        />
      ))}

      {headerExtras ? headerExtras : null}
    </Box>
  ) : null);

  const leadingIconNode = React.useMemo(() => {
    if (leadingNode) {
      return leadingNode;
    }

    const iconComponent = resolvedPresentation.entryIconComponent || null;
    const iconGlyph = resolvedPresentation.entryIcon || resolvedPresentation.icon || null;
    if (!iconComponent && !iconGlyph) {
      return null;
    }

    const iconBoxSize = isStoryVariant ? 44 : compact ? 28 : 32;
    const iconSize = isStoryVariant ? 20 : compact ? 15 : 16;

    return (
      <Box
        sx={{
          width: iconBoxSize,
          height: iconBoxSize,
          borderRadius: isStoryVariant ? '13px' : '12px',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          bgcolor: typePalette?.bg || alpha(resolvedAccentColor, isStoryVariant ? 0.14 : 0.1),
          color: typePalette?.dot || resolvedAccentColor,
        }}
      >
        {iconComponent ? (
          React.createElement(iconComponent, {
            sx: {
              fontSize: iconSize,
              color: typePalette?.dot || resolvedAccentColor,
            },
          })
        ) : (
          <Typography
            component="span"
            sx={{
              fontSize: isStoryVariant ? '1.08rem' : compact ? '0.95rem' : '1rem',
              lineHeight: 1,
              fontWeight: 700,
              color: typePalette?.dot || resolvedAccentColor,
            }}
          >
            {iconGlyph === '•' ? '•' : iconGlyph || '•'}
          </Typography>
        )}
      </Box>
    );
  }, [compact, leadingNode, isStoryVariant, resolvedAccentColor, resolvedPresentation.entryIcon, resolvedPresentation.entryIconComponent, resolvedPresentation.icon, typePalette?.bg, typePalette?.dot]);

  const resolvedHeaderNode = headerNode || (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: isStoryVariant ? 1.05 : 0.85, minWidth: 0, pr: showActions ? (isStoryVariant ? 2.5 : 2.0) : 0 }}>
      {leadingIconNode}
      <Box sx={{ minWidth: 0, flex: 1, pt: isStoryVariant ? 0.1 : 0.05 }}>
        {titleLineNode}
        {resolvedMetaNode ? <Box sx={{ mt: isStoryVariant ? 0.45 : 0.32, minWidth: 0 }}>{resolvedMetaNode}</Box> : null}
      </Box>
    </Box>
  );
  const interactiveHeaderNode = onHeaderClick ? (
    <ButtonBase
      onClick={onHeaderClick}
      aria-label={ariaLabel || title}
      aria-expanded={headerExpanded == null ? undefined : headerExpanded}
      sx={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        borderRadius: '14px',
      }}
    >
      {resolvedHeaderNode}
    </ButtonBase>
  ) : resolvedHeaderNode;

  const resolvedBodyNode = bodyNode || (children ? (
    <Box sx={{ minWidth: 0, mt: isStoryVariant ? 0.2 : densityStyles.bodyMarginTop }}>
      {children}
    </Box>
  ) : null);

  const footerNode = !isStoryVariant && (normalizedTags.length > 0 || actor) ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mt: densityStyles.tagsMarginTop,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: densityStyles.tagsGap,
          minWidth: 0,
          flex: '1 1 auto',
        }}
      >
        {normalizedTags.slice(0, 3).map((tag, index) => (
          <Chip
            key={`${tag.label}-${index}`}
            label={tag.label}
            size="small"
            title={tag.title || undefined}
            sx={{
              height: compact ? 22 : 24,
              px: 0.35,
              fontSize: compact ? '0.68rem' : '0.7rem',
              fontWeight: 800,
              letterSpacing: '0.01em',
              color: tag.color || TEXT_SECONDARY,
              bgcolor: tag.bg || alpha(resolvedAccentColor, 0.1),
              border: `1px solid ${tag.border || alpha(resolvedAccentColor, 0.14)}`,
              '& .MuiChip-label': {
                px: compact ? 0.55 : 0.65,
              },
            }}
          />
        ))}
      </Box>

      {actor ? (
        <Tooltip title={actorTooltip} arrow>
        <Avatar
          src={actor.src || actor.image || null}
          alt={actorTooltip}
          sx={{
            width: compact ? 20 : 22,
            height: compact ? 20 : 22,
            border: `2px solid ${alpha('#FFFFFF', 0.92)}`,
            bgcolor: actorRoleBg,
            color: actorRoleColor,
            fontSize: compact ? '0.54rem' : '0.6rem',
            fontWeight: 800,
            boxShadow: 'none',
            flexShrink: 0,
            ml: 0.5,
            ...actor.sx,
          }}
        >
          {actor.initials || actor.label?.slice?.(0, 2) || ''}
        </Avatar>
        </Tooltip>
      ) : null}
    </Box>
  ) : null;

  return (
    <CareLogEntryCard
      id={id}
      data-entry-id={dataEntryId || undefined}
      aria-label={ariaLabel}
      category={typeKey || 'log'}
      tone={shellTone}
      leftRailColor={railColor}
      leftRailWidth={4}
      leftRailTop={0}
      leftRailBottom={0}
      leftRailOpacity={1}
      leftRailSx={{
        left: 0,
        width: 4,
        borderTopLeftRadius: 18,
        borderBottomLeftRadius: 18,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        backgroundImage: `linear-gradient(180deg, ${alpha('#FFFFFF', 0.34)} 0%, ${alpha('#FFFFFF', 0.05)} 18%, ${alpha('#000000', 0.02)} 100%), linear-gradient(180deg, ${alpha(railColor, 0.82)} 0%, ${alpha(railColor, 0.98)} 52%, ${alpha(railColor, 0.9)} 100%)`,
        boxShadow: `inset -1px 0 0 ${alpha('#FFFFFF', 0.18)}`,
      }}
      showLeftRail={!isStoryVariant}
      variant={variant}
      compact={compact}
      contentSpacing={isStoryVariant ? 0 : 1.05}
      sx={{
        width: '100%',
        maxWidth: '100%',
        alignSelf: 'flex-start',
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        gap: { xs: 0.85, sm: 1.05 },
      boxShadow: 'none',
      overflow: isStoryVariant ? 'hidden' : 'hidden',
      boxSizing: 'border-box',
      ...variantStyles.root,
      ...densityStyles.root,
      ...sx,
    }}
      topRightNode={topRightNode}
      headerNode={interactiveHeaderNode}
      titleNode={null}
      bodyNode={resolvedBodyNode}
      footerNode={footerNode}
    />
  );
};

export default React.memo(TimelineEntryShell);
